import { MouseEventHandler } from "react";
import FollowButtonOffer from "components/FollowButtonOffer/FollowButtonOffer";
import { resolveFollowButtonState } from "components/FollowButtonOffer/followButtonState";
import useFavoriteExpert from "hooks/useFavoriteExpert";
import { getIconSocialMedia, getTopLevelDomain, translate } from "utils";

interface EventOrganizerLinksProps {
  publicId?: string | null;
  name?: string | null;
  headline?: string | null;
  avatar?: string | null;
  email?: string | null;
  phone?: string | null;
  socialNetworks?: string[] | null;
  compact?: boolean;
  expertId?: string;
  isFollowed?: boolean;
  handleFollow?: MouseEventHandler<HTMLButtonElement>;
  handleUnfollow?: MouseEventHandler<HTMLButtonElement>;
  showFollow?: boolean;
  showProfileLinkLabel?: boolean;
}

const hasValue = (value?: string | null) => Boolean(value?.trim());

const eventOrganizerSocialIcons = {
  facebookAlt: (
    <span
      aria-hidden="true"
      className="icon-[mdi--facebook] h-5 w-5 shrink-0 text-current"
    />
  ),
  instagram: (
    <span
      aria-hidden="true"
      className="icon-[mdi--instagram] h-5 w-5 shrink-0 text-current"
    />
  ),
  linkedin: (
    <span
      aria-hidden="true"
      className="icon-[mdi--linkedin] h-5 w-5 shrink-0 text-current"
    />
  ),
  tiktok: (
    <span
      aria-hidden="true"
      className="icon-[simple-icons--tiktok] h-5 w-5 shrink-0 text-current"
    />
  ),
  twitter: (
    <span
      aria-hidden="true"
      className="icon-[ri--twitter-x-fill] h-5 w-5 shrink-0 text-current"
    />
  ),
  youtube: (
    <span
      aria-hidden="true"
      className="icon-[mdi--youtube] h-5 w-5 shrink-0 text-current"
    />
  ),
};

const getEventOrganizerSocialIcon = (url: string) => {
  const domainName = getTopLevelDomain(url);

  return (
    eventOrganizerSocialIcons[
      domainName as keyof typeof eventOrganizerSocialIcons
    ] || getIconSocialMedia(url)
  );
};

const avatarSizeClassName = (compact: boolean) =>
  compact
    ? "h-10 w-10 rounded-full object-cover"
    : "h-12 w-12 rounded-full object-cover";

const organizerTitleClassName = ({
  compact,
  canShowFollow,
  isOrganizerFollowed,
}: {
  compact: boolean;
  canShowFollow: boolean;
  isOrganizerFollowed: boolean;
}) => {
  if (compact) {
    return "mb-2 text-base text-slate-500";
  }

  if (canShowFollow && isOrganizerFollowed) {
    return "mb-3 pr-14 text-base font-semibold text-slate-900";
  }

  return "mb-3 text-base font-semibold text-slate-900";
};

const followButtonContainerClassName = ({
  compact,
  isOrganizerFollowed,
}: {
  compact: boolean;
  isOrganizerFollowed: boolean;
}) => {
  if (compact) {
    return "ml-auto shrink-0 sm:ml-4";
  }

  if (isOrganizerFollowed) {
    return "absolute right-0 top-0 shrink-0";
  }

  return "flex w-full shrink-0 justify-end sm:ml-4 sm:w-auto";
};

function EventOrganizerLinks({
  publicId,
  name,
  headline,
  avatar,
  email,
  phone,
  socialNetworks,
  compact = false,
  expertId,
  isFollowed = false,
  handleFollow,
  handleUnfollow,
  showFollow = false,
  showProfileLinkLabel = false,
}: EventOrganizerLinksProps) {
  const links = (socialNetworks || []).filter(hasValue);
  const hasContacts = hasValue(email) || hasValue(phone);
  const isExpertFavorite = useFavoriteExpert(expertId || "")?.isFavorite;
  const { following: isOrganizerFollowed } = resolveFollowButtonState({
    isFollowed,
    isExpertFavorite,
  });
  const canShowFollow = Boolean(showFollow && handleFollow);

  if (
    !publicId &&
    !name &&
    !headline &&
    !avatar &&
    !hasContacts &&
    !links.length &&
    !canShowFollow
  ) {
    return null;
  }

  return (
    <section
      className={compact ? "mb-4 text-sm" : "relative mt-6 mb-6 text-sm"}
    >
      <p
        className={organizerTitleClassName({
          compact,
          canShowFollow,
          isOrganizerFollowed,
        })}
      >
        {translate("hostedBy") || "Hosted by"}
        {compact ? ":" : ""}
      </p>
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {avatar && publicId && !showProfileLinkLabel ? (
            <a
              href={`/${publicId}`}
              className="shrink-0 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              aria-label={
                name ||
                translate("viewOrganizerProfile") ||
                "View organizer profile"
              }
            >
              <img
                src={avatar}
                alt=""
                className={avatarSizeClassName(compact)}
              />
            </a>
          ) : avatar ? (
            <img src={avatar} alt="" className={avatarSizeClassName(compact)} />
          ) : null}

          <div className="min-w-0">
            {publicId && !showProfileLinkLabel ? (
              <a
                href={`/${publicId}`}
                className="block min-w-0 rounded-lg hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                <span className="inline-flex max-w-full items-center gap-1 font-semibold text-slate-900">
                  <span className="truncate">
                    {name ||
                      translate("viewOrganizerProfile") ||
                      "View organizer profile"}
                  </span>
                  <span
                    aria-hidden="true"
                    className="icon-[material-symbols--arrow-outward] h-5 w-5 shrink-0 text-slate-400"
                  />
                </span>
                {headline ? (
                  <span className="mt-0.5 block text-slate-500">
                    {headline}
                  </span>
                ) : null}
              </a>
            ) : (
              <>
                {name ? (
                  <p className="font-semibold text-slate-900">{name}</p>
                ) : null}
                {publicId && showProfileLinkLabel ? (
                  <a
                    href={`/${publicId}`}
                    className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
                  >
                    <span>
                      {translate("viewOrganizerProfile") ||
                        "View organizer profile"}
                    </span>
                    <span
                      aria-hidden="true"
                      className="icon-[material-symbols--arrow-outward] h-4 w-4 shrink-0 text-slate-400"
                    />
                  </a>
                ) : null}
                {headline ? (
                  <p className="mt-0.5 text-slate-500">{headline}</p>
                ) : null}
              </>
            )}

            {links.length ? (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {links.map((socialNetwork) => (
                  <a
                    key={socialNetwork}
                    href={socialNetwork}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-5 w-5 items-center justify-center text-slate-400 hover:text-slate-600"
                    title={socialNetwork}
                    aria-label={socialNetwork}
                  >
                    {getEventOrganizerSocialIcon(socialNetwork)}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {canShowFollow ? (
          <div
            className={followButtonContainerClassName({
              compact,
              isOrganizerFollowed,
            })}
          >
            <FollowButtonOffer
              handleFollow={handleFollow!}
              handleUnfollow={handleUnfollow}
              expertId={expertId}
              isFollowed={isFollowed}
              outline
              variant="danger"
              iconOnlyWhenFollowed
              size={compact ? "default" : "largeMobile"}
            />
          </div>
        ) : null}
      </div>

      {hasContacts ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {email ? (
            <a
              href={`mailto:${email}`}
              className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-900 hover:bg-slate-200"
            >
              {email}
            </a>
          ) : null}
          {phone ? (
            <a
              href={`tel:${phone}`}
              className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-900 hover:bg-slate-200"
            >
              {phone}
            </a>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default EventOrganizerLinks;
